import React, { useCallback } from "react";
import { getPortalName, useIsTravelApp } from "@capone/common";
import dayjs from "dayjs";
import {
  GenericDetailsCard,
  GenericDetailsCardComponent,
  GenericDetailsCardComponentEnum,
  GenericDetailsCardTopSectionComponentEnum,
  useDeviceTypes,
} from "halifax";
import {
  HotelCfarContract,
  HOTEL_CFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_TABLE,
} from "redmond";

import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import * as constants from "../CfarHotelModalContent/constants";
import { PATH_TERMS_HOTELS_CFAR } from "../../../../../../../../utils/paths";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

interface CfarHotelExpiredModalContentProps {
  cfar: HotelCfarContract | undefined;
  onClose: () => void;
  onConfirm: () => void;
}
export const CfarHotelExpiredModalContent: React.FC<
  CfarHotelExpiredModalContentProps
> = ({ cfar, onClose, onConfirm }) => {
  const { matchesMobile } = useDeviceTypes();

  const { isTravelApp } = useIsTravelApp();

  const getReadTermsLink = (
    className?: string,
  ): GenericDetailsCardComponent => ({
    className,
    message: constants.READ_TERMS_AND_CONDITIONS,
    onClick: () => window.open(PATH_TERMS_HOTELS_CFAR, "_blank")?.focus(),
    position: "left",
    component: GenericDetailsCardComponentEnum.ClickableLink,
  });

  const getTermsAndConditions = useCallback(
    (className?: string) =>
      (matchesMobile
        ? {
            className,
            content: [
              {
                title: constants.READ_TERMS_AND_CONDITIONS,
                body:
                  HOTEL_CFAR_TERMS_BODY(getPortalName()) +
                  HOTEL_CFAR_TERMS_TABLE,
              },
            ],
            component: GenericDetailsCardComponentEnum.AccordionGroup,
          }
        : getReadTermsLink(className)) satisfies GenericDetailsCardComponent,
    [matchesMobile],
  );

  const getCfarExpiredCopy = () => {
    if (!cfar) {
      return "Your Cancel for any reason option has expired. You can still cancel if your plans have changed, but your reservation is nonrefundable.";
    }

    const { expired, expiredTz } = cfar;
    const expirationDateTimeString = dayjs(expired)
      .tz(expiredTz)
      .format("MMM DD, YYYY [at] h:mm A z");

    return `Your Cancel for any reason option expired on ${expirationDateTimeString}. You can still cancel if your plans have changed, but your reservation is nonrefundable.`;
  };

  return (
    <GenericDetailsCard
      contentClassName="cfar-hotel-expired-modal-content-content"
      topContent={{
        header: constants.CANCEL_FOR_ANY_REASON_EXPIRED_TITLE,
        component: GenericDetailsCardTopSectionComponentEnum.GenericHeader,
      }}
      mainContent={[
        {
          className: "cfar-hotel-expired-information",
          type: "secondary",
          copy: getCfarExpiredCopy(),
          component: GenericDetailsCardComponentEnum.GenericCopy,
        },
        getTermsAndConditions("content"),
        {
          message: constants.CONTINUE_CANCELLATION_BUTTON_COPY,
          onClick: onConfirm,
          size: "small",
          component: GenericDetailsCardComponentEnum.GenericCta,
        },
        ...(isTravelApp
          ? []
          : ([
              {
                message: constants.GO_BACK,
                onClick: onClose,
                className: "cfar-hotel-expired-go-back-button",
                position: "center",
                component: GenericDetailsCardComponentEnum.ClickableLink,
              },
            ] as GenericDetailsCardComponent[])),
      ]}
      openModal
      isMobile={matchesMobile}
      contentOnly
      scrollOption="default-scroll"
    />
  );
};
